import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { eventDay } from '../lib/eventStats';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useCreateActivation from '@/src/Activations/components/hooks/useCreateActivation';
import useActivations from '@/src/Activations/components/hooks/useActivations';
import FilterMenu, {
    ActiveFilter,
    FilterDimension,
    dateDimension,
} from '../components/FilterMenu';
import { toast } from 'sonner';
import { interactiveRow } from '../components/a11y';

const IconLead = (
    <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="8" cy="5.5" r="2.8" />
        <path d="M2.8 13.5a5.2 5.2 0 0 1 10.4 0" />
    </svg>
);

const IconLocation = (
    <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M13 6.7c0 3.5-5 8-5 8s-5-4.5-5-8a5 5 0 0 1 10 0Z" />
        <circle cx="8" cy="6.7" r="1.8" />
    </svg>
);

/*
 * Activations v2 — the big-table archetype on live data.
 * Design: docs/design-system/mocks/activations.html. One control: the
 * filter pill (Date presets + custom range, Lead, Location — tags/
 * status join when their endpoints land). Default view = all non-
 * archived, upcoming soonest-first then past newest-first; a Date
 * filter includes archived (past ranges are mostly archived). List
 * search lives in the topbar Jump menu.
 */

interface ActivationRow {
    id: number;
    name: string;
    date: string | Date;
    location: string;
    owner: string;
    isArchived: boolean;
}

function proximity(date: moment.Moment): string {
    const today = moment().startOf('day');
    const d = date.clone().startOf('day');
    const days = d.diff(today, 'days');
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days === -1) return 'Yesterday';
    if (days > 1 && days <= 7) return 'This week';
    if (days > 7 && days <= 21) return `In ${Math.round(days / 7)} weeks`;
    if (days > 21 && days <= 60) return 'Next month';
    if (days < -1 && days >= -7) return 'Last week';
    return d.format('YYYY');
}

export default function ActivationsV2() {
    const { getActivations } = useActivations();
    const authenticatedApi = useAuthenticatedApi();
    const { cloneActivation } = useCreateActivation();

    const rowAction = async (
        e: React.MouseEvent,
        a: any,
        action: 'archive' | 'unarchive' | 'clone',
    ) => {
        e.stopPropagation();
        if (action === 'clone') {
            const name = window.prompt(
                `Clone "${a.name}" — name for the copy:`,
                `${a.name} (copy)`,
            );
            if (!name) return;
            const created = await cloneActivation(
                a.id,
                name.trim(),
                eventDay(a.date).format('YYYY-MM-DD'),
                a.jobId,
            ).catch(() => null);
            if (created?.id) navigate(`/v2/activations/${created.id}`);
            return;
        }
        if (
            action === 'archive' &&
            !window.confirm(`Archive "${a.name}"?`)
        )
            return;
        const ok = await authenticatedApi
            .put(`activations/${a.id}/${action}`)
            .then(() => true)
            .catch(() => false);
        if (!ok) {
            toast.error(`Couldn't ${action} "${a.name}" — try again.`);
            return;
        }
        setRows((rs: any[]) =>
            rs.map((r) =>
                r.id === a.id
                    ? { ...r, isArchived: action === 'archive' }
                    : r,
            ),
        );
    };
    const navigate = useNavigate();
    const [rows, setRows] = useState<ActivationRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [filters, setFilters] = useState<ActiveFilter[]>([]);

    const load = () => {
        setIsLoading(true);
        setLoadError(false);
        getActivations()
            .then((data: ActivationRow[]) => setRows(data ?? []))
            .catch(() => setLoadError(true))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dimensions: FilterDimension[] = useMemo(() => {
        const distinct = (values: (string | undefined)[]) =>
            Array.from(new Set(values.filter(Boolean) as string[])).sort(
                (a, b) => a.localeCompare(b),
            );
        return [
            dateDimension,
            {
                key: 'lead',
                label: 'Lead',
                icon: IconLead,
                values: distinct(rows.map((r) => r.owner)),
            },
            {
                key: 'location',
                label: 'Location',
                icon: IconLocation,
                values: distinct(rows.map((r) => r.location)),
            },
        ];
    }, [rows]);

    const addFilter = (filter: ActiveFilter) => {
        setFilters((prev) => [
            ...prev.filter((f) => f.key !== filter.key),
            filter,
        ]);
    };

    const removeFilter = (key: string) => {
        setFilters((prev) => prev.filter((f) => f.key !== key));
    };

    const PAGE = 50;
    const [limit, setLimit] = useState(PAGE);
    useEffect(() => {
        setLimit(PAGE);
    }, [filters]);

    const filtered = useMemo(() => {
        /* an explicit date filter includes archived events (past ranges
           are mostly archived) */
        const dateFilter = filters.find((f) => f.key === 'date') as
            | (ActiveFilter & { data?: { from?: string; to?: string } })
            | undefined;
        const result = rows.filter((a) => {
            if (!dateFilter && a.isArchived) return false;
            if (dateFilter?.data) {
                const d = eventDay(a.date);
                if (
                    dateFilter.data.from &&
                    d.isBefore(moment(dateFilter.data.from).startOf('day'))
                )
                    return false;
                if (
                    dateFilter.data.to &&
                    d.isAfter(moment(dateFilter.data.to).endOf('day'))
                )
                    return false;
            }
            for (const f of filters) {
                if (f.key === 'lead' && a.owner !== f.value) return false;
                if (f.key === 'location' && a.location !== f.value)
                    return false;
            }
            return true;
        });
        /* upcoming soonest-first, then past newest-first */
        const today = moment().startOf('day');
        result.sort((a, b) => {
            const da = eventDay(a.date);
            const db = eventDay(b.date);
            const fa = da.isSameOrAfter(today);
            const fb = db.isSameOrAfter(today);
            if (fa !== fb) return fa ? -1 : 1;
            return fa
                ? da.valueOf() - db.valueOf()
                : db.valueOf() - da.valueOf();
        });
        return result;
    }, [rows, filters]);

    return (
        <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-3.5">Activations</div>

            {/* toolbar: the filter pill, right-aligned */}
            <div className="mb-3 flex items-center gap-3">
                <div className="flex-1" />
                <FilterMenu dimensions={dimensions} onAdd={addFilter} />
            </div>

            {/* active filter chips */}
            {filters.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                    {filters.map((f) => (
                        <span
                            key={f.key}
                            title={`${f.label} is ${f.value}`}
                            className="inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-dashed border-border px-[11px] py-[5px] text-xs text-muted-foreground"
                        >
                            <span className="flex text-faint">
                                {dimensions.find((d) => d.key === f.key)?.icon}
                            </span>
                            is{' '}
                            <b className="font-semibold text-foreground">
                                {f.value}
                            </b>
                            <button
                                type="button"
                                aria-label={`Remove ${f.label} filter`}
                                onClick={() => removeFilter(f.key)}
                                className="cursor-pointer border-0 bg-transparent p-0 text-[13px] leading-none text-faint hover:text-foreground"
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {/* table */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px]">
                    <thead>
                        <tr>
                            <th className="ps-label border-b border-border pb-2 pl-2.5 pr-2.5 text-left font-semibold">
                                Date
                            </th>
                            <th className="ps-label border-b border-border pb-2 pr-2.5 text-left font-semibold">
                                Activation
                            </th>
                            <th className="ps-label border-b border-border pb-2 pr-2.5 text-left font-semibold">
                                Lead
                            </th>
                            <th className="border-b border-border" />
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading &&
                            [0, 1, 2, 3, 4].map((i) => (
                                <tr key={i}>
                                    <td colSpan={4} className="px-2.5 py-3">
                                        <div className="h-9 animate-pulse rounded-[9px] bg-secondary" />
                                    </td>
                                </tr>
                            ))}
                        {!isLoading &&
                            filtered.slice(0, limit).map((a) => {
                                const d = eventDay(a.date);
                                return (
                                    <tr
                                        key={a.id}
                                        {...interactiveRow(() =>
                                            navigate(
                                                `/v2/activations/${a.id}`,
                                            ),
                                        )}
                                        className="group cursor-pointer border-b border-line hover:bg-sunk"
                                    >
                                        <td className="w-[190px] py-2.5 pl-2.5 pr-2.5">
                                            <div className="flex items-center gap-2">
                                                <div className="w-11 flex-none rounded-lg border border-border py-1 text-center">
                                                    <div className="font-mono text-[9px] uppercase tracking-[0.06em] text-attention">
                                                        {d.format('MMM')}
                                                    </div>
                                                    <div className="text-sm font-semibold tabular-nums leading-tight">
                                                        {d.format('DD')}
                                                    </div>
                                                </div>
                                                <span className="text-[11.5px] text-faint">
                                                    {proximity(d)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-2.5 pr-2.5">
                                            <div className="font-semibold tracking-[-0.005em]">
                                                {a.name}
                                                {a.isArchived && (
                                                    <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
                                                        archived
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-px text-[11.5px] text-faint">
                                                {a.location || '—'}
                                            </div>
                                        </td>
                                        <td className="py-2.5 pr-2.5 text-[12.5px] text-muted-foreground">
                                            {a.owner || '—'}
                                        </td>
                                        <td className="w-[150px] whitespace-nowrap py-2.5 pr-1 text-right">
                                            <span className="inline-flex gap-3 opacity-0 focus-within:opacity-100 group-hover:opacity-100">
                                                <button
                                                    type="button"
                                                    onClick={(e) =>
                                                        rowAction(
                                                            e,
                                                            a,
                                                            'clone',
                                                        )
                                                    }
                                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-brand-ink"
                                                >
                                                    Clone
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) =>
                                                        rowAction(
                                                            e,
                                                            a,
                                                            a.isArchived
                                                                ? 'unarchive'
                                                                : 'archive',
                                                        )
                                                    }
                                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-faint hover:text-attention"
                                                >
                                                    {a.isArchived
                                                        ? 'Unarchive'
                                                        : 'Archive'}
                                                </button>
                                            </span>
                                            <span className="text-faint group-hover:hidden">
                                                →
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        {!isLoading && filtered.length === 0 && (
                            <tr>
                                <td
                                    colSpan={4}
                                    className="px-2.5 py-10 text-center text-[13px] text-muted-foreground"
                                >
                                    {loadError ? (
                                        <>
                                            Couldn't load activations.{' '}
                                            <button
                                                type="button"
                                                onClick={load}
                                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[13px] font-semibold text-brand-ink"
                                            >
                                                Retry
                                            </button>
                                        </>
                                    ) : filters.length > 0 ? (
                                        'No activations match your filters.'
                                    ) : (
                                        'No activations yet — press + to create one.'
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {!isLoading && (
                <div className="mt-3 flex items-center justify-between text-xs text-faint">
                    <span>
                        {Math.min(limit, filtered.length)} of{' '}
                        {filtered.length} activations
                    </span>
                    {filtered.length > limit && (
                        <button
                            type="button"
                            onClick={() => setLimit((l) => l + PAGE)}
                            className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                        >
                            Show more
                        </button>
                    )}
                </div>
            )}
        </main>
    );
}
